import { useEffect } from "react";

const SITE = process.env.REACT_APP_PUBLIC_SITE_URL;

const upsert = (selector, create) => {
  let el = document.head.querySelector(selector);
  const existed = Boolean(el);
  const previous = el ? el.outerHTML : null;
  if (!el) { el = create(); document.head.appendChild(el); }
  return { el, restore: () => { if (existed) el.outerHTML = previous; else el.remove(); } };
};

const meta = (attr, key, content) => {
  const { el, restore } = upsert(`meta[${attr}="${key}"]`, () => { const m = document.createElement("meta"); m.setAttribute(attr, key); return m; });
  el.setAttribute("content", content);
  return restore;
};

// Per-page head tags (title, description, canonical, Open Graph, robots, JSON-LD). Restores the defaults on unmount.
// The deployed site's crawler prerender captures these, so bots receive them in the HTML.
export const useHead = ({ title, description, path, image, type = "website", jsonLd, notFound = false }) => {
  const ld = jsonLd ? (typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd)) : null;
  useEffect(() => {
    const cleanups = [];
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) cleanups.push(meta("name", "description", description));
    if (title) cleanups.push(meta("property", "og:title", title));
    if (description) cleanups.push(meta("property", "og:description", description));
    if (image) cleanups.push(meta("property", "og:image", image));
    cleanups.push(meta("property", "og:type", type));
    if (SITE && path && !notFound) {
      const url = `${SITE.replace(/\/$/, "")}${path}`;
      cleanups.push(meta("property", "og:url", url));
      const { el, restore } = upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; });
      el.href = url;
      cleanups.push(restore);
    }
    if (notFound) {
      cleanups.push(meta("name", "robots", "noindex"));
      cleanups.push(meta("name", "prerender-status-code", "404"));
    }
    if (ld) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.page = "page";
      s.text = ld;
      document.head.appendChild(s);
      cleanups.push(() => s.remove());
    }
    return () => { document.title = prevTitle; cleanups.reverse().forEach((c) => c()); };
  }, [title, description, path, image, type, ld, notFound]);
};
